<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { todayKey } from '@/utils/date'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton } from '@/design-system/components'
import { classesApi } from '@/api'
import { getColour } from '@/constants/colors'
import DatePicker from '@/components/inputs/DatePicker.vue'

const props = defineProps<{
  modelValue: boolean
  classId?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: [event: {
    title: string
    startDateTime: string
    endDateTime?: string
    allDay?: boolean
    location?: string
    description?: string
    classId?: string
    className?: string
    classColour?: string
  }]
}>()

interface EventForm {
  title: string
  date: string
  allDay: boolean
  startTime: string
  endTime: string
  location: string
  description: string
  scope: 'school' | 'class'
  classId: string
}

const form = ref<EventForm>({
  title: '',
  date: todayKey(),
  allDay: false,
  startTime: '09:00',
  endTime: '10:00',
  location: '',
  description: '',
  scope: 'school',
  classId: '',
})

const saving = ref(false)
const classes = ref<Array<{ id: string; name: string; colour?: string }>>([])
const loadingClasses = ref(false)

const effectiveClassId = computed(() => {
  if (props.classId) return props.classId
  if (form.value.scope === 'class') return form.value.classId
  return undefined
})

const selectedClass = computed(() => classes.value.find(c => c.id === effectiveClassId.value))

const canSave = computed(() => {
  if (!form.value.title.trim()) return false
  if (!form.value.date) return false
  if (!form.value.allDay && !form.value.startTime) return false
  if (form.value.scope === 'class' && !effectiveClassId.value) return false
  return true
})

async function loadClasses() {
  if (props.classId || classes.value.length > 0) return
  loadingClasses.value = true
  try {
    const res = await classesApi.classesGet()
    classes.value = (res.data as any[]).map((c: any) => ({ id: c.id, name: c.name, colour: c.colour }))
  } catch { /* ignore */ } finally {
    loadingClasses.value = false
  }
}

function close() {
  emit('update:modelValue', false)
}

function resetForm() {
  form.value = {
    title: '',
    date: todayKey(),
    allDay: false,
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    description: '',
    scope: props.classId ? 'class' : 'school',
    classId: '',
  }
}

function handleSubmit() {
  if (!canSave.value || saving.value) return

  saving.value = true

  const startDateTime = form.value.allDay
    ? `${form.value.date}T00:00:00`
    : `${form.value.date}T${form.value.startTime}:00`

  const endDateTime = form.value.allDay
    ? undefined
    : form.value.endTime
      ? `${form.value.date}T${form.value.endTime}:00`
      : undefined

  emit('created', {
    title: form.value.title.trim(),
    startDateTime,
    endDateTime,
    allDay: form.value.allDay || undefined,
    location: form.value.location.trim() || undefined,
    description: form.value.description.trim() || undefined,
    classId: effectiveClassId.value,
    className: selectedClass.value?.name,
    classColour: selectedClass.value?.colour,
  })

  saving.value = false
  close()
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      resetForm()
      loadClasses()
    }
  }
)
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 flex items-start justify-center pt-[5vh] p-4">
        <div class="absolute inset-0 bg-black/30 backdrop-blur-[2px]" @click="close" />

        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
          <!-- Header -->
          <div class="px-6 pt-6 pb-4">
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'calendar-day']" class="text-purple-500 text-sm" />
                </div>
                <h2 class="text-lg font-bold text-gray-900">Add Event</h2>
              </div>
              <button class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" @click="close">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
          </div>

          <!-- Form -->
          <form class="px-6 pb-2 space-y-5 overflow-y-auto flex-1 min-h-0" @submit.prevent="handleSubmit">
            <!-- Title -->
            <div>
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Title</label>
              <input
                v-model="form.title"
                type="text"
                required
                placeholder="e.g. End of Term Assembly"
                class="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-colors"
              />
            </div>

            <!-- Date -->
            <div>
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Date</label>
              <DatePicker v-model="form.date" placeholder="Pick a date" />
            </div>

            <!-- All day toggle -->
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                :class="form.allDay ? 'bg-purple-500' : 'bg-gray-200'"
                @click="form.allDay = !form.allDay"
              >
                <span
                  class="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform shadow-sm"
                  :class="form.allDay ? 'translate-x-[18px]' : 'translate-x-[3px]'"
                />
              </button>
              <span class="text-sm text-gray-700 font-medium">All day</span>
            </div>

            <!-- Time (hidden when all day) -->
            <div v-if="!form.allDay">
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Time</label>
              <div class="flex gap-2 items-center">
                <input
                  v-model="form.startTime"
                  type="time"
                  required
                  class="w-[120px] px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 text-center focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-colors"
                />
                <span class="text-gray-300 text-sm">-</span>
                <input
                  v-model="form.endTime"
                  type="time"
                  class="w-[120px] px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 text-center focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-colors"
                />
              </div>
            </div>

            <!-- Location -->
            <div>
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Location</label>
              <input
                v-model="form.location"
                type="text"
                placeholder="e.g. Main Hall"
                class="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-colors"
              />
            </div>

            <!-- Description -->
            <div>
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Description</label>
              <textarea
                v-model="form.description"
                rows="2"
                placeholder="Add details..."
                class="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-colors resize-none"
              />
            </div>

            <!-- Scope -->
            <div v-if="!classId">
              <label class="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Scope</label>
              <div class="flex gap-2 mb-3">
                <button
                  type="button"
                  class="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors text-center"
                  :class="form.scope === 'school'
                    ? 'border-purple-300 bg-purple-50/60 text-purple-700'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'"
                  @click="form.scope = 'school'"
                >
                  School-wide
                </button>
                <button
                  type="button"
                  class="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors text-center"
                  :class="form.scope === 'class'
                    ? 'border-purple-300 bg-purple-50/60 text-purple-700'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'"
                  @click="form.scope = 'class'"
                >
                  Class only
                </button>
              </div>

              <!-- Class selector (when scope is class) -->
              <div v-if="form.scope === 'class'">
                <div v-if="loadingClasses" class="flex items-center gap-2 text-sm text-gray-400 py-2">
                  <font-awesome-icon :icon="['fas', 'spinner']" class="animate-spin text-xs" />
                  Loading...
                </div>
                <div v-else class="grid grid-cols-2 gap-1.5">
                  <button
                    v-for="cls in classes"
                    :key="cls.id"
                    type="button"
                    class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-all border"
                    :class="form.classId === cls.id
                      ? 'border-purple-300 bg-purple-50/60 shadow-sm'
                      : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'"
                    @click="form.classId = cls.id"
                  >
                    <div class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: getColour(cls.colour).hex }" />
                    <span class="text-sm font-medium text-gray-800 truncate">{{ cls.name }}</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Selected class pill (when classId provided) -->
            <div v-else-if="selectedClass" class="flex items-center gap-2">
              <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: getColour(selectedClass.colour).hex }" />
              <span class="text-sm font-medium text-gray-600">{{ selectedClass.name }}</span>
            </div>
          </form>

          <!-- Footer -->
          <div class="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-gray-100 shrink-0">
            <AppButton
              variant="secondary"
              size="md"
              @click="close"
            >
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              size="md"
              :disabled="!canSave"
              :loading="saving"
              @click="handleSubmit"
            >
              Create Event
            </AppButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
